import { FeeMarketEIP1559Transaction, Transaction } from '@exodus/ethereumjs/tx'
import { padToEven, toBuffer } from '@exodus/ethereumjs/util'
import baseX from 'base-x'

// eslint-disable-next-line @exodus/import/no-deprecated
import { ETHEREUM_LIKE_NO_HISTORY_ASSET_NAMES } from '../constants.js'

export { default as calculateExtraEth } from './calculate-extra-eth.js'

const base10 = baseX('0123456789')
const base16 = baseX('0123456789abcdef')

/* @deprecated */
export const isEthereumToken = (asset) => asset.assetType === 'ETHEREUM_ERC20'
/* @deprecated */
export const isEthereumGoerliToken = (asset) => asset.assetType === 'ETHEREUM_GOERLI_ERC20'
/* @deprecated */
export const isEthereumHoleskyToken = (asset) => asset.assetType === 'ETHEREUM_HOLESKY_ERC20'
/* @deprecated */
export const isEthereumSepoliaToken = (asset) => asset.assetType === 'ETHEREUM_SEPOLIA_ERC20'
/* @deprecated */
export const isBscToken = (asset) => asset.assetType === 'BSC_BEP20'
/* @deprecated */
export const isPolygonToken = (asset) => asset.assetType === 'MATIC_ERC20'
/* @deprecated */
export const isAvalancheToken = (asset) => asset.assetType === 'AVAX_ERC20'
/* @deprecated */
export const isFantomToken = (asset) => asset.assetType === 'FTM_ERC20'
/* @deprecated */
export const isArbitrumNovaToken = (asset) => asset.assetType === 'ETHEREUM_ARBNOVA_ERC20'
/* @deprecated */
export const isArbitrumOneToken = (asset) => asset.assetType === 'ETHEREUM_ARBONE_ERC20'
/* @deprecated */
export const isRootstockToken = (asset) => asset.assetType === 'RSK_ERC20'
/* @deprecated */
export const isOptimismToken = (asset) => asset.assetType === 'OPT_ERC20'
/* @deprecated */
export const isFlareToken = (asset) => asset.assetType === 'FLR_ERC20'
/* @deprecated */
export const isAuroraToken = (asset) => asset.assetType === 'AURORA_ERC20'
/* @deprecated */
export const isCronosToken = (asset) => asset.assetType === 'CRC20'
/* @deprecated */
export const isBaseMainnetToken = (asset) => asset.assetType === 'BASE_ERC20'

// All ethereum-like tokens
export const isEthereumLikeToken = (asset) =>
  isEthereumLikeAsset(asset.baseAsset) && asset.baseAsset.name !== asset.name

// @deprecated
export const isToken = isEthereumLikeToken

// All ethereum-like assets (native coins)
export const isEthereumLikeAsset = (asset) => asset.assetType === 'ETHEREUM_LIKE'

// All ethereum-like assets and tokens
export const isEthereumLike = (asset) => isEthereumLikeAsset(asset.baseAsset)
// @deprecated
export const isEthereumLikeByName = (assetName) => {
  throw new Error('Please use isEthereumLike() instead')
}

export function buffer2currency({ asset, value }) {
  return asset.currency.baseUnit(base10.encode(value))
}

export function currency2buffer(value) {
  const b10Value = value.toBaseString()

  // Desktop: const hexValue = new BN(value.toBaseString()).toString(16)
  const hexValue = base16.encode(base10.decode(b10Value))

  if (hexValue.includes('.')) {
    // <-- probably not necessary anymore
    throw new RangeError(`${value.toBaseString()} can not be converted to Buffer`)
  }

  return Buffer.from(padToEven(hexValue), 'hex')
}

export function normalizeTxId(txId) {
  if (txId.startsWith('0x')) return txId
  return '0x' + txId
}

// @deprecated
export function getBaseAsset(asset) {
  throw new Error('getBaseAsset: this function is no longer supported')
}

const TRANSACTION_TYPE = 2
const TRANSACTION_TYPE_BUFFER = Buffer.from(TRANSACTION_TYPE.toString(16).padStart(2, '0'), 'hex')

export function deserialize(hex) {
  const buf = toBuffer(hex)
  return buf.slice(0, 1).equals(TRANSACTION_TYPE_BUFFER)
    ? FeeMarketEIP1559Transaction.fromSerializedTx(buf)
    : Transaction.fromSerializedTx(buf)
}

const getTxType = (serializedTx) => serializedTx.slice(2, 4)
export const isEip1559Tx = (serializedTx) => getTxType(serializedTx) === '02'

export const APPROVE_METHOD_ID = '0x095ea7b3'

export const KNOWN_NFT_TRANSFER_METHOD_IDS = [
  '0xfb0f3ee1', // NFT marketplace seaport fulfillBasicOrder methodId
]

const ERC721_TRANSFER_METHOD_IDS = [
  '0x23b872dd', // transferFrom(address,address,uint256)
  '0x42842e0e', // safeTransferFrom(address,address,uint256)
  '0xb88d4fde', // safeTransferFrom(address,address,uint256,bytes)
  '0x66a3de2a', // _safeTransferFrom(address,address,uint256,bytes)
]

export const checkIsNFTInputData = (data) =>
  KNOWN_NFT_TRANSFER_METHOD_IDS.some((methodId) => data?.startsWith(methodId))

export const checkIsERC721InputData = (data) =>
  ERC721_TRANSFER_METHOD_IDS.some((methodId) => data?.startsWith(methodId))

export const FEE_PAYMENT_PREFIX = '0xfee0'

export const isApproveTx = (tx) => tx?.data?.data?.startsWith(APPROVE_METHOD_ID)

export const isFeePaymentTx = (tx) => tx?.data?.data?.startsWith(FEE_PAYMENT_PREFIX)

const customTokensWithRpcBalance = [
  'ampl_ethereum_48428467',
  'feg_ethereum_ef42acf0',
  'floki_ethereum_4a3891a6',
  'mark_ethereum_0d0c07dd',
  'opt2_matic_2bb66719',
  'saitama_ethereum_5547ffb6',
  'shiryoinu_ethereum_ff507c93',
  'volt_ethereum_9e0778ce',
  'wavax_avalanchec_635505b3',
  'wftm_fantommainnet_7edc4949',
  'wmatic_matic_424fae45',
]

export const isRpcBalanceAsset = (asset) =>
  [
    'ethereumarbone',
    'fantommainnet',
    'matic',
    'optimism',
    'ousd_ethereum_48fcf72d',
    'steth',
    'weth',
    ...customTokensWithRpcBalance,
    // eslint-disable-next-line @exodus/import/no-deprecated
  ].includes(asset.name) || ETHEREUM_LIKE_NO_HISTORY_ASSET_NAMES.includes(asset.baseAsset.name)

export const getAssetAddresses = (asset) => {
  // It seems to be two schemas of assets. The original and the transformed by the client.
  // Can we unify?
  const addresses = [
    asset.contract?.address, // asset in wallets:
    asset.contract?.current, // asset in @exodus/assets-base:
    ...(asset.addresses?.previous || []), // asset in wallets:
    ...(asset.contract?.previous || []), // asset in @exodus/assets-base:
  ]
  return [...new Set(addresses.filter((a) => a && typeof a === 'string'))]
}
